/*
 * MySiteCare
 *
 * Copyright © 2026 Jeanjen Sisoy Luberio
 * doing business as MySiteCare Website Application Services.
 * All rights reserved.
 *
 * DTI Business Name No. 7934250
 * https://mysitecare.eu
 *
 * This file contains original proprietary MySiteCare source code,
 * except for third-party components identified under their
 * respective licenses.
 */
import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import {
  ShieldCheck, LogOut, Activity, AlertTriangle, CheckCircle2, Clock, ChevronDown, ChevronUp,
  DatabaseBackup, Mail, RefreshCw, Check, Globe, Bug, Zap, PauseCircle, PlayCircle,
  Sparkles, XCircle,
} from "lucide-react";
import { useAdminAuth } from "@/hooks/useAdminAuth";
import {
  fetchMonitoringOverview, fetchNeedsManualAction, fetchMonitoringIncidentDetail,
  resolveMonitoringIncident, acknowledgeMonitoringIncident, fetchBackupStatus, fetchEmailStatus,
  runManualBackup, fetchMonitoringReleaseHistory, fetchSiteCoverage, fetchAutomationOverview,
  approveRecovery, declineRecovery, pauseSiteAutomation, resumeSiteAutomation,
  startRepair, fetchRepairRuns, approveRepair, declineRepair,
} from "@/lib/api";
import AdminNavTabs from "@/components/AdminNavTabs";
import AiGuardianPanel, { AiIncidentAssist } from "@/components/AiGuardianPanel";
import AiSupportCopilotPanel from "@/components/AiSupportCopilotPanel";

const SEVERITY_STYLE = {
  critical: "bg-rose-100 text-rose-800 border-rose-300",
  high: "bg-orange-100 text-orange-800 border-orange-300",
  warning: "bg-amber-100 text-amber-800 border-amber-300",
  info: "bg-slate-100 text-slate-600 border-slate-300",
};

const URGENCY_LABEL = { immediate: "Immediate", urgent: "Urgent", soon: "Soon", normal: "Normal", low: "Low" };

const AUTOMATION_STATE_STYLE = {
  recovered: "bg-emerald-100 text-emerald-800 border-emerald-300",
  running: "bg-sky-100 text-sky-800 border-sky-300",
  eligible: "bg-sky-100 text-sky-800 border-sky-300",
  requires_approval: "bg-amber-100 text-amber-800 border-amber-300",
  failed: "bg-rose-100 text-rose-800 border-rose-300",
  aborted: "bg-slate-100 text-slate-600 border-slate-300",
  paused: "bg-slate-100 text-slate-600 border-slate-300",
  not_eligible: "bg-slate-100 text-slate-500 border-slate-300",
};
const AUTOMATION_STATE_LABEL = {
  not_eligible: "Not eligible", eligible: "Eligible", queued: "Queued", running: "Running",
  verifying: "Verifying", recovered: "Auto-recovered", failed: "Automation failed",
  aborted: "Aborted", paused: "Paused", requires_approval: "Awaiting approval",
};

function formatDuration(fromIso, toIso) {
  if (!fromIso) return "—";
  const from = new Date(fromIso).getTime();
  const to = toIso ? new Date(toIso).getTime() : Date.now();
  const minutes = Math.max(0, Math.round((to - from) / 60000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

function formatAbsolute(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString();
}

function Badge({ children, className }) {
  return <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${className}`}>{children}</span>;
}

// ---------------------------------------------------------------------------
// Phase C — constrained AI-assisted diagnosis and repair. AI never gets a route of
// its own to production here: every state below is a read-only report of what
// repair_engine.py already decided; the only two writes a human can trigger from
// this panel are Start (diagnose/patch/validate — never deploys) and
// Approve/Decline (and even Approve never deploys unless a real deployment
// mechanism is genuinely configured — see the "Production Deployment: NOT
// CONFIGURED" message this reports honestly today).
// ---------------------------------------------------------------------------
const REPAIR_STATE_STYLE = {
  not_eligible: "bg-slate-100 text-slate-500 border-slate-300",
  insufficient_evidence: "bg-slate-100 text-slate-500 border-slate-300",
  diagnosing: "bg-sky-100 text-sky-800 border-sky-300",
  patching: "bg-sky-100 text-sky-800 border-sky-300",
  validating: "bg-sky-100 text-sky-800 border-sky-300",
  validation_failed: "bg-rose-100 text-rose-800 border-rose-300",
  awaiting_approval: "bg-amber-100 text-amber-800 border-amber-300",
  ready_to_release: "bg-amber-100 text-amber-800 border-amber-300",
  resolved: "bg-emerald-100 text-emerald-800 border-emerald-300",
  cancelled: "bg-slate-100 text-slate-500 border-slate-300",
  failed: "bg-rose-100 text-rose-800 border-rose-300",
};
const REPAIR_STATE_LABEL = {
  not_eligible: "Not eligible", insufficient_evidence: "Insufficient evidence", diagnosing: "Diagnosing",
  patching: "Preparing patch", validating: "Validating", validation_failed: "Validation failed",
  awaiting_approval: "Awaiting approval", ready_to_release: "Ready — awaiting release",
  resolved: "Approved & released", cancelled: "Declined", failed: "Failed",
};
const RISK_STYLE = {
  R1: "bg-emerald-100 text-emerald-800 border-emerald-300",
  R2: "bg-amber-100 text-amber-800 border-amber-300",
  R3: "bg-rose-100 text-rose-800 border-rose-300",
};

function RepairDiff({ patch }) {
  if (!patch?.files?.length) return null;
  return (
    <div className="mt-2 space-y-2">
      {patch.files.map((f) => (
        <div key={f.file_path} className="rounded-lg border border-slate-200">
          <p className="border-b border-slate-200 bg-slate-50 px-2 py-1 font-mono text-[11px] text-slate-600">{f.file_path}</p>
          {/* Diffs scroll inside their own container — never the page body (spec §69). */}
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-words p-2 text-[11px] leading-snug text-slate-700" data-testid="repair-diff">
            {f.unified_diff}
          </pre>
        </div>
      ))}
    </div>
  );
}

function AIRepairSection({ incident, token }) {
  const [runs, setRuns] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showDiff, setShowDiff] = useState(false);

  const load = useCallback(async () => {
    const data = await fetchRepairRuns(token, incident.id).catch(() => null);
    setRuns(data?.repair_runs || []);
    setLoaded(true);
  }, [token, incident.id]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incident.id]);

  const handleStart = async () => {
    setBusy(true);
    try {
      await startRepair(token, incident.id);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = async (runId) => {
    setBusy(true);
    try {
      await approveRepair(token, runId);
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async (runId) => {
    setBusy(true);
    try {
      await declineRepair(token, runId, "Declined from the incident's AI Repair panel.");
      await load();
    } finally {
      setBusy(false);
    }
  };

  if (!loaded) return null;
  const latest = runs?.[0] || null;

  return (
    <div className="mt-3 rounded-lg border border-slate-200 p-3" data-testid="ai-repair-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <Sparkles size={13} /> AI Repair
        </p>
        {latest && (
          <Badge className={REPAIR_STATE_STYLE[latest.state] || REPAIR_STATE_STYLE.not_eligible}>
            {REPAIR_STATE_LABEL[latest.state] || latest.state}
          </Badge>
        )}
      </div>

      {!latest && (
        <div className="mt-2 flex items-center justify-between gap-2">
          <p className="text-xs text-slate-500">No repair has been attempted for this incident yet.</p>
          <button
            onClick={handleStart}
            disabled={busy}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            data-testid="start-repair-button"
          >
            <Sparkles size={13} /> Diagnose with AI
          </button>
        </div>
      )}

      {latest && (
        <div className="mt-2 space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-xs">
            <span className="text-slate-400">Provider:</span>
            <span className="font-mono text-slate-700">{latest.diagnosis?.provider || "—"}</span>
            {latest.diagnosis?.provider === "test_adapter" && (
              <Badge className="bg-indigo-100 text-indigo-800 border-indigo-300">TEST ADAPTER — not production AI</Badge>
            )}
            {latest.diagnosis?.confidence && (
              <span className="text-slate-400">
                Confidence: <span className="font-semibold text-slate-700">{latest.diagnosis.confidence}</span>
              </span>
            )}
            {latest.diagnosis?.risk_level && (
              <Badge className={RISK_STYLE[latest.diagnosis.risk_level] || RISK_STYLE.R3}>{latest.diagnosis.risk_level}</Badge>
            )}
          </div>

          {latest.diagnosis?.root_cause_summary && (
            <p className="text-sm text-slate-700"><strong>Root cause:</strong> {latest.diagnosis.root_cause_summary}</p>
          )}
          {latest.final_result && (
            <p className="text-sm text-slate-600"><strong>Result:</strong> {latest.final_result}</p>
          )}
          {latest.pre_patch_reproduction && latest.pre_patch_reproduction !== "not_attempted" && (
            <p className="text-xs text-slate-500">
              Pre-patch reproduction: <span className="font-semibold">{latest.pre_patch_reproduction}</span>
            </p>
          )}

          {latest.validations?.length > 0 && (
            <ul className="space-y-0.5 text-xs">
              {latest.validations.map((v, i) => (
                <li key={i} className="flex items-center gap-1.5">
                  {v.passed ? <CheckCircle2 size={12} className="text-emerald-600" /> : <XCircle size={12} className="text-rose-600" />}
                  <span className="text-slate-600">{v.check_name.replace(/_/g, " ")}</span>
                </li>
              ))}
            </ul>
          )}

          {latest.patch?.files?.length > 0 && (
            <button onClick={() => setShowDiff((v) => !v)} className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline">
              {showDiff ? <ChevronUp size={13} /> : <ChevronDown size={13} />} {showDiff ? "Hide" : "Show"} proposed diff
            </button>
          )}
          {showDiff && <RepairDiff patch={latest.patch} />}

          <div className="flex flex-wrap gap-2 pt-1">
            {(latest.state === "awaiting_approval" || latest.state === "ready_to_release") && (
              <>
                <button onClick={() => handleApprove(latest.id)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="approve-repair-button">
                  Approve
                </button>
                <button onClick={() => handleDecline(latest.id)} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="decline-repair-button">
                  Decline
                </button>
              </>
            )}
            {(latest.state === "validation_failed" || latest.state === "not_eligible" || latest.state === "failed" || latest.state === "cancelled") && (
              <button onClick={handleStart} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="retry-repair-button">
                <RefreshCw size={13} /> Retry
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function IncidentCard({ incident, token, onChanged }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (!expanded && !detail) {
      const d = await fetchMonitoringIncidentDetail(token, incident.id).catch(() => null);
      setDetail(d);
    }
    setExpanded((v) => !v);
  };

  const handleResolve = async () => {
    setBusy(true);
    try {
      await resolveMonitoringIncident(token, incident.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const handleAcknowledge = async () => {
    setBusy(true);
    try {
      await acknowledgeMonitoringIncident(token, incident.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const handleApproveRecovery = async () => {
    setBusy(true);
    try {
      await approveRecovery(token, incident.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const handleDeclineRecovery = async () => {
    setBusy(true);
    try {
      await declineRecovery(token, incident.id);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const handlePauseAutomation = async () => {
    setBusy(true);
    try {
      await pauseSiteAutomation(token, incident.id, "Paused from incident detail.");
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  const handleResumeAutomation = async () => {
    if (!incident.site_slug) return;
    setBusy(true);
    try {
      await resumeSiteAutomation(token, incident.site_slug);
      onChanged();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="monitoring-incident-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge className={SEVERITY_STYLE[incident.severity] || SEVERITY_STYLE.info}>{incident.severity?.toUpperCase()}</Badge>
          <Badge className="bg-slate-100 text-slate-700 border-slate-300">{URGENCY_LABEL[incident.urgency] || incident.urgency}</Badge>
          {incident.site_slug && <span className="text-xs font-semibold text-slate-500">{incident.site_slug}</span>}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleAcknowledge} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
            <Check size={13} /> Acknowledge
          </button>
          <button onClick={handleResolve} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50">
            <CheckCircle2 size={13} /> Resolve
          </button>
        </div>
      </div>

      <h3 className="mt-3 font-semibold text-slate-900">{incident.title}</h3>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
        <div><p className="text-slate-400 uppercase tracking-wide">Started</p><p className="mt-0.5 font-medium text-slate-700">{formatAbsolute(incident.first_seen)}</p></div>
        <div><p className="text-slate-400 uppercase tracking-wide">Duration</p><p className="mt-0.5 font-medium text-slate-700">{formatDuration(incident.first_seen, incident.resolved_at)}</p></div>
        <div><p className="text-slate-400 uppercase tracking-wide">Occurrences</p><p className="mt-0.5 font-medium text-slate-700">{incident.occurrence_count}</p></div>
        <div><p className="text-slate-400 uppercase tracking-wide">Affected sessions</p><p className="mt-0.5 font-medium text-slate-700">{incident.affected_sessions?.length ?? 0}</p></div>
        <div><p className="text-slate-400 uppercase tracking-wide">Route</p><p className="mt-0.5 font-medium text-slate-700 truncate" title={incident.route}>{incident.route || "—"}</p></div>
        <div><p className="text-slate-400 uppercase tracking-wide">Error code</p><p className="mt-0.5 font-medium text-slate-700">{incident.protocol}:{incident.raw_code ?? "—"}</p></div>
        <div><p className="text-slate-400 uppercase tracking-wide">Release</p><p className="mt-0.5 font-medium text-slate-700 truncate" title={incident.releases_seen?.slice(-1)[0]}>{incident.releases_seen?.slice(-1)[0] || "—"}</p></div>
        <div><p className="text-slate-400 uppercase tracking-wide">Last seen</p><p className="mt-0.5 font-medium text-slate-700">{formatAbsolute(incident.last_seen)}</p></div>
      </div>

      <div className="mt-4 rounded-lg bg-slate-50 p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
          Diagnosis <span className="ml-1 font-normal normal-case text-slate-400">({incident.root_cause_confidence || "low"} confidence, rule-based)</span>
        </p>
        <p className="mt-1 text-sm text-slate-700">{incident.root_cause_summary}</p>
      </div>

      <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
        <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Why this needs you</p>
        <p className="mt-1 text-sm text-amber-900">{incident.manual_action_reason}</p>
        {incident.suggested_action && <p className="mt-1 text-sm text-amber-900"><strong>Suggested:</strong> {incident.suggested_action}</p>}
      </div>

      <div className="mt-3 rounded-lg border border-slate-200 p-3" data-testid="incident-automation-section">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500">
            <Zap size={13} /> Automation
          </p>
          <Badge className={AUTOMATION_STATE_STYLE[incident.automation_state] || AUTOMATION_STATE_STYLE.not_eligible}>
            {AUTOMATION_STATE_LABEL[incident.automation_state] || AUTOMATION_STATE_LABEL.not_eligible}
          </Badge>
        </div>
        {incident.automation_action_id && (
          <p className="mt-2 text-sm text-slate-700"><strong>Recovery action:</strong> {incident.automation_action_id}</p>
        )}
        {incident.recovery_attempts?.length > 0 && (
          <p className="mt-1 text-sm text-slate-700">
            <strong>Attempts:</strong> {incident.recovery_attempts.length}
            {" · "}Last result: {incident.recovery_attempts[incident.recovery_attempts.length - 1].result}
            {incident.recovery_attempts[incident.recovery_attempts.length - 1].verification_result && (
              <> (verification: {incident.recovery_attempts[incident.recovery_attempts.length - 1].verification_result})</>
            )}
          </p>
        )}
        {incident.automation_reason && (
          <p className="mt-1 text-sm text-slate-500"><strong>Why:</strong> {incident.automation_reason}</p>
        )}
        <div className="mt-2 flex flex-wrap gap-2">
          {incident.automation_state === "requires_approval" && (
            <>
              <button onClick={handleApproveRecovery} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700 disabled:opacity-50" data-testid="approve-recovery-button">
                Approve
              </button>
              <button onClick={handleDeclineRecovery} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="decline-recovery-button">
                Decline
              </button>
            </>
          )}
          {incident.site_slug && incident.automation_state !== "recovered" && (
            <button onClick={handlePauseAutomation} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="pause-automation-button">
              <PauseCircle size={13} /> Pause Automation
            </button>
          )}
          {incident.site_slug && (
            <button onClick={handleResumeAutomation} disabled={busy} className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50" data-testid="resume-automation-button">
              <PlayCircle size={13} /> Resume Automation
            </button>
          )}
        </div>
      </div>

      <AIRepairSection incident={incident} token={token} />
      <AiIncidentAssist incident={incident} token={token} onChanged={onChanged} />

      <button onClick={toggle} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 hover:underline">
        {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />} {expanded ? "Hide" : "Show"} recent events & raw detail
      </button>
      {expanded && detail && (
        <div className="mt-3 border-t border-slate-100 pt-3">
          {detail.http_meaning && (
            <p className="text-xs text-slate-500 mb-2">
              <strong>{detail.http_meaning.name}</strong> — {detail.http_meaning.meaning}
            </p>
          )}
          <ul className="space-y-1.5 text-xs text-slate-600 max-h-56 overflow-y-auto">
            {(detail.recent_events || []).map((ev) => (
              <li key={ev.id} className="font-mono">
                {ev.occurred_at} · {ev.protocol}:{ev.raw_code || "—"} · {ev.route || "—"} {ev.request_id ? `· req ${ev.request_id.slice(0, 8)}` : ""}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default function AdminMonitoring() {
  const { token, email, checking, isLoggedIn, revealAdminUi, logout } = useAdminAuth();
  const [overview, setOverview] = useState(null);
  const [queue, setQueue] = useState(null);
  const [backup, setBackup] = useState(null);
  const [emailStatus, setEmailStatus] = useState(null);
  const [backupRunning, setBackupRunning] = useState(false);
  const [releases, setReleases] = useState(null);
  const [releasesOpen, setReleasesOpen] = useState(false);
  const [coverage, setCoverage] = useState(null);
  const [automation, setAutomation] = useState(null);

  const load = useCallback(() => {
    if (!token) return;
    fetchMonitoringOverview(token).then(setOverview).catch(() => {});
    fetchNeedsManualAction(token).then(setQueue).catch(() => {});
    fetchBackupStatus(token).then(setBackup).catch(() => {});
    fetchEmailStatus(token).then(setEmailStatus).catch(() => {});
    fetchMonitoringReleaseHistory(token).then(setReleases).catch(() => {});
    fetchSiteCoverage(token).then(setCoverage).catch(() => {});
    fetchAutomationOverview(token).then(setAutomation).catch(() => {});
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Carry-forward acceptance item: a REAL browser-triggered runtime error, not a
  // synthetic dispatchEvent. This literally throws inside a real event handler in a
  // real running tab — the SAME window.onerror handler lib/monitoring.js wires up for
  // every real visitor is what catches this, exactly as it would a genuine bug.
  // Admin-only (this whole page is admin-gated) — mirrors the existing backend
  // /api/admin/test-monitoring/trigger-500 utility rather than a one-off console throw,
  // so this proof stays reproducible for any later regression check.
  const handleTriggerFrontendError = () => {
    throw new Error("Deliberate test failure — triggered via AdminMonitoring's \"Trigger test frontend error\" button");
  };

  const handleManualBackup = async () => {
    setBackupRunning(true);
    try {
      await runManualBackup(token);
      load();
    } finally {
      setBackupRunning(false);
    }
  };

  if (checking) return null;
  if (!revealAdminUi) return <Navigate to="/admin/login" replace />;

  const counts = overview?.severity_counts || {};

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-16 text-slate-900">
      <div className="mx-auto max-w-5xl">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2.5 text-emerald-700"><ShieldCheck size={20} /><span className="text-xs font-bold uppercase tracking-[0.16em]">{isLoggedIn ? `Logged in as ${email}` : "DEBUG — Admin UI revealed, no real session"}</span></div>
          <button type="button" onClick={logout} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-100"><LogOut size={15} /> Log out</button>
        </div>
        <h1 className="mt-4 font-display text-3xl md:text-4xl font-bold">Admin</h1>
        <AdminNavTabs />

        <div className="mt-8 flex items-center justify-between">
          <h2 className="flex items-center gap-2 text-xl font-bold"><Activity size={20} className="text-emerald-600" /> Operations</h2>
          <button onClick={load} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-slate-800"><RefreshCw size={13} /> Refresh</button>
        </div>

        {!overview ? (
          <p className="mt-4 text-sm text-slate-500">Loading…</p>
        ) : (
          <>
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="monitoring-severity-counts">
              {["critical", "high", "warning", "info"].map((sev) => (
                <div key={sev} className={`rounded-xl border p-4 ${SEVERITY_STYLE[sev]}`}>
                  <p className="text-2xl font-bold font-display">{counts[sev] ?? 0}</p>
                  <p className="text-xs font-semibold uppercase tracking-wide">{sev}</p>
                </div>
              ))}
            </div>

            <div className="mt-4 grid sm:grid-cols-3 gap-4">
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Events, last 24h</p>
                <p className="mt-1 text-2xl font-bold font-display">{overview.events_last_24h}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Resolved, last 24h</p>
                <p className="mt-1 text-2xl font-bold font-display">{overview.resolved_last_24h}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Monitoring itself</p>
                <p className={`mt-1 text-sm font-bold ${overview.monitoring_self_health?.is_stale ? "text-rose-600" : "text-emerald-700"}`}>
                  {overview.monitoring_self_health?.is_stale ? "STALE — checks may not be running" : "Running"}
                </p>
              </div>
            </div>

            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="monitoring-backup-card">
                <h3 className="flex items-center gap-2 font-semibold"><DatabaseBackup size={17} className="text-emerald-600" /> Backups</h3>
                {backup ? (
                  <>
                    <p className="mt-2 text-sm text-slate-600">
                      Latest: {backup.has_any_backup ? formatAbsolute(backup.latest_successful_backup?.started_at) : "None yet"}
                      {backup.is_stale && backup.has_any_backup && <span className="ml-1 font-semibold text-rose-600">(stale)</span>}
                    </p>
                    <p className="mt-1 text-sm text-slate-600">
                      Restore verified: {backup.has_any_verified_restore ? formatAbsolute(backup.latest_verified_restore?.restore_verified_at) : <span className="font-semibold text-amber-600">Never</span>}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">Storage: {backup.storage_backend}</p>
                    <button onClick={handleManualBackup} disabled={backupRunning} className="mt-3 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50">
                      {backupRunning ? "Running…" : "Run backup now"}
                    </button>
                  </>
                ) : <p className="mt-2 text-sm text-slate-500">Loading…</p>}
              </div>
              <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="monitoring-email-card">
                <h3 className="flex items-center gap-2 font-semibold"><Mail size={17} className="text-emerald-600" /> Email</h3>
                {emailStatus ? (
                  <>
                    <p className="mt-2 text-sm text-slate-600">Provider: <strong>{emailStatus.provider === "none" ? "Not configured" : emailStatus.provider}</strong></p>
                    <p className="mt-1 text-sm">
                      {emailStatus.production_activation === "external_blocker" ? (
                        <span className="font-semibold text-amber-600">External blocker — no production provider credentials yet</span>
                      ) : (
                        <span className="font-semibold text-emerald-700">Active</span>
                      )}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">{emailStatus.total_send_attempts} send attempt(s) logged, {emailStatus.failed_count} failed</p>
                  </>
                ) : <p className="mt-2 text-sm text-slate-500">Loading…</p>}
              </div>
            </div>

            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="monitoring-site-coverage-card">
              <h3 className="flex items-center gap-2 font-semibold"><Globe size={17} className="text-emerald-600" /> Site Coverage</h3>
              <p className="mt-1 text-xs text-slate-400">
                How many managed sites actually have each feature configured — never implied by a healthy queue alone.
              </p>
              {coverage ? (
                <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  <div><p className="text-2xl font-bold font-display">{coverage.production_sites}</p><p className="text-xs text-slate-500">Live client sites (of {coverage.total_sites} total)</p></div>
                  <div><p className="text-2xl font-bold font-display">{coverage.with_connected_domain}</p><p className="text-xs text-slate-500">With a connected custom domain</p></div>
                  <div><p className="text-2xl font-bold font-display">{coverage.with_live_contact_form}</p><p className="text-xs text-slate-500">With a live contact form ({coverage.with_verified_contact_form} delivery-verified)</p></div>
                  <div><p className="text-2xl font-bold font-display">{coverage.with_appointments}</p><p className="text-xs text-slate-500">With appointment booking</p></div>
                  <div><p className="text-2xl font-bold font-display">{coverage.with_commerce}</p><p className="text-xs text-slate-500">With commerce/checkout</p></div>
                </div>
              ) : <p className="mt-2 text-sm text-slate-500">Loading…</p>}
            </div>

            <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5" data-testid="monitoring-automation-card">
              <h3 className="flex items-center gap-2 font-semibold"><Zap size={17} className="text-emerald-600" /> Automation</h3>
              {automation ? (
                <>
                  <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
                    <div><p className="text-2xl font-bold font-display text-emerald-700">{automation.auto_recovered_today}</p><p className="text-xs text-slate-500">Auto-recovered today</p></div>
                    <div><p className={`text-2xl font-bold font-display ${automation.automation_failed > 0 ? "text-rose-600" : ""}`}>{automation.automation_failed}</p><p className="text-xs text-slate-500">Automation failed</p></div>
                    <div><p className={`text-2xl font-bold font-display ${automation.awaiting_approval > 0 ? "text-amber-600" : ""}`}>{automation.awaiting_approval}</p><p className="text-xs text-slate-500">Awaiting approval</p></div>
                    <div><p className={`text-2xl font-bold font-display ${automation.tripped_site_count > 0 ? "text-amber-600" : ""}`}>{automation.tripped_site_count}</p><p className="text-xs text-slate-500">Sites automation-paused</p></div>
                  </div>
                  {automation.global_tripped && (
                    <p className="mt-3 rounded-lg bg-rose-50 border border-rose-200 p-2 text-xs font-semibold text-rose-700">
                      Platform-wide automatic recovery is paused (global circuit breaker). {automation.global_tripped_reason}
                    </p>
                  )}
                </>
              ) : <p className="mt-2 text-sm text-slate-500">Loading…</p>}
            </div>
          </>
        )}

        <AiGuardianPanel token={token} />
        <AiSupportCopilotPanel token={token} />
        <h2 className="mt-10 flex items-center gap-2 text-xl font-bold"><AlertTriangle size={20} className="text-amber-600" /> Needs Manual Action</h2>
        <p className="mt-1 text-sm text-slate-500">Automation already tried everything it's safely authorized to do for everything below. A human is genuinely needed.</p>

        <div className="mt-4 space-y-4" data-testid="needs-manual-action-list">
          {!queue ? (
            <p className="text-sm text-slate-500">Loading…</p>
          ) : queue.length === 0 ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center">
              <CheckCircle2 size={22} className="mx-auto text-emerald-600" />
              <p className="mt-2 font-semibold text-emerald-800">Everything healthy. No action required.</p>
            </div>
          ) : (
            queue.map((incident) => <IncidentCard key={incident.id} incident={incident} token={token} onChanged={load} />)
          )}
        </div>

        {overview && (
          <div className="mt-6 text-xs text-slate-400">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="flex items-center gap-1.5"><Clock size={12} /> Current release marker: <span className="font-mono">{overview.current_release_id}</span></p>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleTriggerFrontendError}
                  className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-rose-700"
                  data-testid="trigger-test-frontend-error"
                  title="Deliberately throws a real error in this browser tab to prove the monitoring pipeline end to end — see Milestone 1's backend equivalent, /api/admin/test-monitoring/trigger-500"
                >
                  <Bug size={13} /> Trigger test frontend error
                </button>
                <button
                  type="button"
                  onClick={() => setReleasesOpen((v) => !v)}
                  className="inline-flex items-center gap-1 font-semibold text-slate-500 hover:text-slate-800"
                  data-testid="release-history-toggle"
                >
                  {releasesOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />} Release history
                </button>
              </div>
            </div>
            {releasesOpen && (
              <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3" data-testid="release-history-list">
                <p className="text-slate-400">
                  Recorded from real backend process boots — this codebase has no CI/CD deploy feed, so a "release" here means a
                  genuine process restart, not a fabricated deploy event.
                </p>
                {!releases ? (
                  <p className="mt-2 text-slate-500">Loading…</p>
                ) : releases.length === 0 ? (
                  <p className="mt-2 text-slate-500">No release recorded yet.</p>
                ) : (
                  <ul className="mt-2 space-y-1 font-mono">
                    {releases.map((r) => (
                      <li key={r.id} className="truncate">{formatAbsolute(r.first_observed_at)} — {r.release_id}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
